import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { ArrowLeft, Camera, Check, ChevronRight, Loader2, Plus, Trash2, X } from "lucide-react";

const FONT_MAIN = "'Hanken Grotesk', 'Inter', sans-serif";

const glassCard = {
  background: "rgba(40, 42, 50, 0.6)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(141, 144, 160, 0.5)",
  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
};

const TIPOS = [
  { v: "solicitacao", l: "Solicitação" },
  { v: "reclamacao", l: "Reclamação" },
  { v: "sugestao", l: "Sugestão" },
  { v: "elogio", l: "Elogio" },
];
const PRIORIDADES = [
  { v: "baixa", l: "Baixa" },
  { v: "media", l: "Média" },
  { v: "alta", l: "Alta" },
  { v: "urgente", l: "Urgente" },
];
const TIPO_LABEL = { solicitacao: "Solicitação", reclamacao: "Reclamação", sugestao: "Sugestão", elogio: "Elogio", manutencao: "Manutenção", outros: "Outros" };
const STATUS_LABEL = { aberto: "Aberto", em_analise: "Em Análise", em_andamento: "Em Andamento", resolvido: "Resolvido", cancelado: "Cancelado" };
const STATUS_COR = { aberto: "#8ab4ff", em_analise: "#f4a261", em_andamento: "#f4a261", resolvido: "#7ef0a0", cancelado: "#8d90a0" };

const inputClass = "w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#b4c5ff] focus:ring-1 focus:ring-[#b4c5ff] transition-all placeholder:text-[#c3c6d7]/70";
const inputStyle = {
  background: "rgba(255, 255, 255, 0.1)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.2)", color: "#e1e2ed", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
};

function fmtData(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) + " " +
      d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function Chip({ ativo, cor = "#b4c5ff", onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all active:scale-95"
      style={ativo
        ? { background: `${cor}2e`, color: cor, border: `1px solid ${cor}66` }
        : { background: "rgba(255,255,255,0.05)", color: "#8d90a0", border: "1px solid rgba(255,255,255,0.1)" }}>
      {children}
    </button>
  );
}

export default function MobileSolicitacoes({ origem, eql, cpf, onClose }) {
  const [itens, setItens] = useState(null);
  const [tela, setTela] = useState("lista"); // lista | form | detalhe
  const [sel, setSel] = useState(null);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [verTodos, setVerTodos] = useState(false);
  const [pareceres, setPareceres] = useState(null);
  const [form, setForm] = useState({ tipo: "solicitacao", assunto: "", descricao: "", prioridade: "media" });
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const fileRef = useRef(null);

  const qs = `origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(eql)}&cpf=${cpf}`;

  const carregar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/minhas-manifestacoes?${qs}`);
      const d = await r.json();
      if (d.ok) setItens(d.itens); else setItens([]);
    } catch { setItens([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origem, eql, cpf]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirDetalhe = async (m) => {
    setSel(m); setTela("detalhe"); setConfirmCancel(false); setPareceres(null);
    try {
      const r = await fetch(`${API}/public/manifestacoes/${m.id}?${qs}`);
      const d = await r.json();
      if (d.ok) { setPareceres(d.pareceres || []); if (d.item) setSel((s) => ({ ...s, ...d.item })); }
      else setPareceres([]);
    } catch { setPareceres([]); }
  };

  const novoForm = () => {
    setEditando(null);
    setForm({ tipo: "solicitacao", assunto: "", descricao: "", prioridade: "media" });
    setFoto(null); setFotoPreview(null);
    setTela("form");
  };

  const editarForm = (m) => {
    setEditando(m);
    setForm({ tipo: m.tipo, assunto: m.assunto, descricao: m.descricao, prioridade: m.prioridade });
    setFoto(null); setFotoPreview(null);
    setTela("form");
  };

  const escolherFoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Foto excede o limite de 10 MB"); return; }
    setFoto(f);
    setFotoPreview(URL.createObjectURL(f));
  };

  const uploadFoto = async () => {
    if (!foto) return editando ? (editando.attachments || []) : [];
    const fd = new FormData();
    fd.append("file", foto);
    fd.append("origem", origem); fd.append("eql", eql); fd.append("cpf", cpf);
    const r = await fetch(`${API}/public/manifestacoes-upload`, { method: "POST", body: fd });
    const d = await r.json();
    if (!d.ok) throw new Error(d.motivo || "upload");
    return [d.storage_path];
  };

  const salvar = async () => {
    if (!form.assunto.trim() || !form.descricao.trim()) {
      toast.error("Informe o assunto e a descrição."); return;
    }
    setSalvando(true);
    try {
      const attachments = await uploadFoto();
      const payload = { origem, eql, cpf, ...form, attachments };
      const url = editando ? `${API}/public/manifestacoes/${editando.id}` : `${API}/public/manifestacoes`;
      const r = await fetch(url, {
        method: editando ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success(editando ? "Registro atualizado" : "Registro enviado à administração");
        setTela("lista"); setSel(null);
        carregar();
      } else {
        toast.error(d.motivo === "status" ? "Somente registros Abertos podem ser alterados." : "Não foi possível salvar. Tente novamente.");
      }
    } catch {
      toast.error("Falha de comunicação. Tente novamente.");
    } finally { setSalvando(false); }
  };

  const cancelar = async () => {
    setSalvando(true);
    try {
      const r = await fetch(`${API}/public/manifestacoes/${sel.id}/cancelar`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origem, eql, cpf }),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success("Registro cancelado");
        setConfirmCancel(false); setTela("lista"); setSel(null);
        carregar();
      } else {
        toast.error("Somente registros Abertos podem ser cancelados.");
      }
    } catch { toast.error("Falha de comunicação. Tente novamente."); }
    finally { setSalvando(false); }
  };

  const tipoLabel = (v) => TIPO_LABEL[v] || v;
  const prioLabel = (v) => PRIORIDADES.find((p) => p.v === v)?.l || v;

  const emAndamento = (itens || []).filter((m) => m.status !== "resolvido" && m.status !== "cancelado");
  const finalizados = (itens || []).filter((m) => m.status === "resolvido" || m.status === "cancelado");
  const lista = verTodos ? [...emAndamento, ...finalizados] : emAndamento;

  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-solic-overlay">
      <header className="flex items-center gap-3 px-4 py-3.5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <button type="button" data-testid="appmobile-solic-voltar" aria-label="Voltar"
          onClick={() => (tela === "lista" ? onClose() : (setTela("lista"), setSel(null), setConfirmCancel(false)))}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-base font-bold" style={{ color: "#b4c5ff" }} data-testid="appmobile-solic-titulo">
          {tela === "form" ? (editando ? "Editar Registro" : "Novo Registro") : tela === "detalhe" ? `#${String(sel?.number || 0).padStart(4, "0")}` : "Solicitações"}
        </h1>
        {tela === "lista" && (
          <div className="ml-auto flex items-center gap-2">
            <button type="button" data-testid="appmobile-solic-ver-todos" onClick={() => setVerTodos(!verTodos)}
              className="rounded-xl px-3.5 py-2 text-xs font-semibold active:scale-[0.97] transition-transform"
              style={{ color: "#b4c5ff", background: "rgba(180,197,255,0.1)", border: "1px solid rgba(180,197,255,0.3)" }}>
              {verTodos ? "Em andamento" : "Ver todos"}
            </button>
            <button type="button" data-testid="appmobile-solic-novo" onClick={novoForm}
              className="flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold text-white active:scale-[0.97] transition-transform"
              style={{ background: "#2563EB", boxShadow: "0 0 12px rgba(37,99,235,0.3)" }}>
              <Plus className="h-4 w-4" /> Novo
            </button>
          </div>
        )}
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-28">
        {/* LISTA */}
        {tela === "lista" && (
          !itens ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#8d90a0" }} /></div>
          ) : lista.length === 0 ? (
            <p className="text-center text-xs py-16" style={{ color: "#8d90a0" }} data-testid="appmobile-solic-vazio">
              {verTodos ? "Nenhum registro para a sua unidade." : "Nenhum registro em andamento para a sua unidade."}
            </p>
          ) : (
            <div className="flex flex-col gap-2" data-testid="appmobile-solic-lista">
              {lista.map((m) => (
                <button key={m.id} type="button" data-testid={`appmobile-solic-item-${m.id}`}
                  onClick={() => abrirDetalhe(m)}
                  className="w-full text-left rounded-xl px-3.5 py-3 relative overflow-hidden active:scale-[0.98] transition-transform"
                  style={glassCard}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0"
                      style={{ background: "rgba(180,197,255,0.12)", color: "#b4c5ff" }}>{tipoLabel(m.tipo)}</span>
                    <span className="flex-1 min-w-0 truncate text-xs font-semibold" style={{ color: "#e1e2ed" }}>{m.assunto}</span>
                    <ChevronRight className="h-4 w-4 shrink-0" style={{ color: "#8d90a0" }} />
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] font-semibold" style={{ color: STATUS_COR[m.status] || "#8d90a0" }}
                      data-testid={`appmobile-solic-status-${m.id}`}>{STATUS_LABEL[m.status] || m.status}</span>
                    <span className="text-[10px]" style={{ color: "#8d90a0" }}>#{String(m.number).padStart(4, "0")} · {prioLabel(m.prioridade)} · {fmtData(m.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          )
        )}

        {/* FORM */}
        {tela === "form" && (
          <div className="flex flex-col gap-4" data-testid="appmobile-solic-form">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "#c3c6d7" }}>Tipo</p>
              <div className="flex flex-wrap gap-2">
                {TIPOS.map((t) => (
                  <Chip key={t.v} testid={`appmobile-solic-tipo-${t.v}`} ativo={form.tipo === t.v}
                    onClick={() => setForm({ ...form, tipo: t.v })}>{t.l}</Chip>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#c3c6d7" }}>Assunto</p>
              <input data-testid="appmobile-solic-assunto" value={form.assunto} maxLength={150}
                onChange={(e) => setForm({ ...form, assunto: e.target.value })}
                placeholder="Resumo do registro" className={inputClass} style={inputStyle} />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#c3c6d7" }}>Descrição</p>
              <textarea data-testid="appmobile-solic-descricao" value={form.descricao} maxLength={2000} rows={4}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                placeholder="Descreva com detalhes" className={`${inputClass} resize-none`} style={inputStyle} />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "#c3c6d7" }}>Prioridade</p>
              <div className="flex flex-wrap gap-2">
                {PRIORIDADES.map((p) => (
                  <Chip key={p.v} testid={`appmobile-solic-prio-${p.v}`} ativo={form.prioridade === p.v}
                    cor={p.v === "urgente" ? "#ffb4ab" : p.v === "alta" ? "#f4a261" : "#b4c5ff"}
                    onClick={() => setForm({ ...form, prioridade: p.v })}>{p.l}</Chip>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "#c3c6d7" }}>Foto (opcional)</p>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                data-testid="appmobile-solic-foto-input" onChange={escolherFoto} />
              {fotoPreview ? (
                <div className="relative w-32">
                  <img src={fotoPreview} alt="Foto selecionada" data-testid="appmobile-solic-foto-preview"
                    className="w-32 h-32 object-cover rounded-xl" style={{ border: "1px solid rgba(255,255,255,0.2)" }} />
                  <button type="button" data-testid="appmobile-solic-foto-remover" aria-label="Remover foto"
                    onClick={() => { setFoto(null); setFotoPreview(null); if (fileRef.current) fileRef.current.value = ""; }}
                    className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: "#ffb4ab", color: "#11131b" }}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : editando && (editando.attachments || [])[0] ? (
                <div className="flex items-center gap-3">
                  <img src={`${API}/public/manifestacao-imagem/${editando.id}?${qs}`} alt="Foto atual"
                    data-testid="appmobile-solic-foto-atual"
                    className="w-32 h-32 object-cover rounded-xl" style={{ border: "1px solid rgba(255,255,255,0.2)" }} />
                  <button type="button" data-testid="appmobile-solic-foto-trocar" onClick={() => fileRef.current?.click()}
                    className="text-xs font-medium px-3 py-2 rounded-xl active:scale-95 transition-transform"
                    style={{ color: "#b4c5ff", background: "rgba(180,197,255,0.1)", border: "1px solid rgba(180,197,255,0.3)" }}>
                    Trocar foto
                  </button>
                </div>
              ) : (
                <button type="button" data-testid="appmobile-solic-foto-add" onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-xs font-medium active:scale-[0.98] transition-transform"
                  style={{ color: "#c3c6d7", background: "rgba(255,255,255,0.05)", border: "1px dashed rgba(255,255,255,0.2)" }}>
                  <Camera className="h-4 w-4" /> Tirar foto ou escolher da galeria
                </button>
              )}
            </div>
            <button type="button" data-testid="appmobile-solic-salvar" onClick={salvar} disabled={salvando}
              className="mt-1 w-full py-3 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-all disabled:opacity-40"
              style={{ background: "#2563EB", boxShadow: "0 0 15px rgba(37,99,235,0.3)" }}>
              {salvando ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : editando ? "Salvar alterações" : "Enviar para a administração"}
            </button>
          </div>
        )}

        {/* DETALHE */}
        {tela === "detalhe" && sel && (
          <div className="flex flex-col gap-4" data-testid="appmobile-solic-detalhe">
            {(sel.attachments || [])[0] && (
              <img src={`${API}/public/manifestacao-imagem/${sel.id}?${qs}`} alt={sel.assunto}
                data-testid="appmobile-solic-detalhe-foto"
                className="w-full rounded-xl object-contain max-h-64" style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)" }} />
            )}
            <div className="rounded-xl p-4 relative overflow-hidden" style={glassCard}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(180,197,255,0.12)", color: "#b4c5ff" }}>{tipoLabel(sel.tipo)}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(244,162,97,0.12)", color: "#f4a261" }}>{prioLabel(sel.prioridade)}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: `${STATUS_COR[sel.status] || "#8d90a0"}22`, color: STATUS_COR[sel.status] || "#8d90a0" }}
                  data-testid="appmobile-solic-detalhe-status">{STATUS_LABEL[sel.status] || sel.status}</span>
              </div>
              <h2 className="text-sm font-bold mt-3" style={{ color: "#e1e2ed" }} data-testid="appmobile-solic-detalhe-assunto">{sel.assunto}</h2>
              <p className="text-xs mt-2 whitespace-pre-wrap" style={{ color: "#c3c6d7" }} data-testid="appmobile-solic-detalhe-descricao">{sel.descricao}</p>
              <p className="text-[10px] mt-3" style={{ color: "#8d90a0" }}>
                Aberto em {fmtData(sel.created_at)}{sel.responsavel_name ? ` · Responsável: ${sel.responsavel_name}` : ""}
              </p>
            </div>
            {sel.resposta_admin && (
              <div className="rounded-xl p-4 relative overflow-hidden" style={glassCard} data-testid="appmobile-solic-parecer">
                <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#7ef0a0" }}>Parecer da administração</p>
                <p className="text-xs whitespace-pre-wrap" style={{ color: "#c3c6d7" }}>{sel.resposta_admin}</p>
              </div>
            )}
            {sel.data_previsao && (
              <div className="rounded-xl p-4 relative overflow-hidden" style={glassCard} data-testid="appmobile-solic-prazo">
                <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#b4c5ff" }}>Prazo de conclusão</p>
                <p className="text-xs font-medium" style={{ color: "#e1e2ed" }}>
                  {new Date(sel.data_previsao).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })}
                </p>
              </div>
            )}
            {pareceres && pareceres.length > 0 && (
              <div className="rounded-xl p-4 relative overflow-hidden" style={glassCard} data-testid="appmobile-solic-pareceres">
                <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "#7ef0a0" }}>Mensagens da administração</p>
                <div className="flex flex-col gap-3">
                  {pareceres.map((p, i) => (
                    <div key={i} data-testid={`appmobile-solic-parecer-${i}`}>
                      <p className="text-xs whitespace-pre-wrap" style={{ color: "#c3c6d7" }}>{p.message || p.texto || ""}</p>
                      <p className="text-[10px] mt-1" style={{ color: "#8d90a0" }}>
                        {(p.sender_name || p.usuario_nome || "Administração")} · {fmtData(p.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sel.status === "aberto" && (
              <div className="flex gap-3">
                <button type="button" data-testid="appmobile-solic-editar" onClick={() => editarForm(sel)}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-transform"
                  style={{ background: "#2563EB" }}>
                  Editar
                </button>
                {!confirmCancel ? (
                  <button type="button" data-testid="appmobile-solic-cancelar" onClick={() => setConfirmCancel(true)}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
                    style={{ color: "#ffb4ab", background: "rgba(255,180,171,0.08)", border: "1px solid rgba(255,180,171,0.3)" }}>
                    <Trash2 className="h-4 w-4" /> Cancelar registro
                  </button>
                ) : (
                  <button type="button" data-testid="appmobile-solic-confirmar-cancelar" onClick={cancelar} disabled={salvando}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-transform disabled:opacity-40"
                    style={{ background: "#dc2626" }}>
                    {salvando ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Confirmar cancelamento"}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
